import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Container,
  Row,
  Col,
  HeaderText,
  BodyText,
  TextIconButton,
  RotateIcon,
  Form,
} from 'shared-portal-component';
import { z } from 'zod';

import '@/components/organism/style.scss';
import { useAppDispatch } from '@/store';
import { resendPhoneCodeThunk } from '@/store/thunks/auth/resendPhoneCodeThunk';
import { confirmPhoneThunk } from '@/store/thunks/auth/confirmPhoneThunk';
import { MIN_LEN } from '@/constants';
import { sendPhoneCodeThunk } from '@/store/thunks/auth/sendPhoneThunk';


const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const schema = z.object({
  code: z
    .string()
    .length(CODE_LENGTH, `Code must be exactly ${CODE_LENGTH} digits`)
    .regex(/^\d+$/, 'Code must contain only digits'),
});

interface PhoneConfirmationProps {
  formData?: { code?: string };
  onNext: (values: { code: string }) => void;
  loading?: boolean;
  error?: string;
}

const PhoneConfirmation: React.FC<PhoneConfirmationProps> = ({ formData, onNext, loading, error }) => {
  const dispatch = useAppDispatch();

  const [resendSecondsRemaining, setResendSecondsRemaining] = useState<number>(RESEND_COOLDOWN_SECONDS);

  const canResend = resendSecondsRemaining <= MIN_LEN && loading !== true;

  const startResendCooldown = useCallback(() => {
    setResendSecondsRemaining(RESEND_COOLDOWN_SECONDS);
  }, []);

  useEffect(() => {
    void dispatch(sendPhoneCodeThunk(undefined));
    startResendCooldown();
  }, [dispatch, startResendCooldown]);

  useEffect(() => {
    if (resendSecondsRemaining <= MIN_LEN) return undefined;

    const intervalId = window.setInterval(() => {
      setResendSecondsRemaining((previousSeconds) => Math.max(previousSeconds - 1, MIN_LEN));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [resendSecondsRemaining]);

  const handleResend = useCallback(() => {
    if (!canResend) return;

    void dispatch(resendPhoneCodeThunk(undefined));
    startResendCooldown();
  }, [canResend, dispatch, startResendCooldown]);

  const handleConfirm = useCallback(
    (values: { code: string }) => {
      if (loading === true) return;

      void (async() => {
        const action = await dispatch(confirmPhoneThunk({ code: values.code }));

        if (confirmPhoneThunk.fulfilled.match(action)) {
          onNext({ code: values.code });
        }
      })();
    },
    [dispatch, loading, onNext]
  );

  const resendLabel =
    resendSecondsRemaining > MIN_LEN ? `Resend in ${String(resendSecondsRemaining)}s` : 'Resend';

  const fields = useMemo(
    () => [
      {
        name: 'code',
        label: 'Code',
        placeholder: 'Enter the 6-digit code',
        required: true,
        variant: 'outline' as const,
        type: 'code',
        defaultValue: formData?.code ?? '',
      },
      {
        type: 'display',
        name: 'resend',
        label: 'resend',
        display: (
          <Row spacing="none" justify="center" className="space-y">
            <TextIconButton
              icon={<RotateIcon />}
              text={resendLabel}
              disabled={!canResend}
              onClick={handleResend}
              variant="outline"
              size="sm"
            />
          </Row>
        ),
      },
    ],
    [canResend, formData?.code, handleResend, resendLabel]
  );

  return (
    <Container fluid>
      <Card>
        <Row spacing="md">
          <Col size={12}>
            <HeaderText text="Confirm phone number" size="h2" className="header-text-auth" />
            <BodyText
              text="Now that we’ve confirmed your email address, let’s verify that we can send you a code via text."
              className="body-text-auth"
              as="p"
            />
            <BodyText
              text="To make sure it’s working, we’ve sent a code to your phone number ending in 2435. Please enter this code below."
              className="body-text-auth"
              as="p"
            />

            {typeof error === 'string' && error.trim().length > MIN_LEN ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive mt-3">
                {error}
              </div>
            ) : null}
          </Col>
        </Row>

        <Row justify="center" spacing="none">
          <Form schema={schema} fields={fields} onSubmit={handleConfirm} />
        </Row>
      </Card>
    </Container>
  );
};

export default PhoneConfirmation;
